import { type AdminClient } from '@apx/admin-sdk';
import { fmtNumber } from '../format.js';
import { descriptorFor, type RtpContributor } from './rtp-descriptor.js';
import { renderLiveRtpCard } from './live-rtp-card.js';
import { renderTargetRtpPanel } from './target-rtp.js';
import { renderWeightsPage } from './weights.js';
import { renderSicboWeightsPage } from './sicbo-weights.js';
import { renderDragontigerWeightsPage } from './dragontiger-weights.js';
import { renderMultipliersPage } from './multipliers.js';
import { renderSicboMultipliersPage } from './sicbo-multipliers.js';
import { renderDragontigerMultipliersPage } from './dragontiger-multipliers.js';
import { renderSingaporeLotteryMultipliersPage } from './singaporelottery-multipliers.js';
import { renderBaccaratMultipliersPage } from './baccarat-multipliers.js';
import { renderDiceRushMultipliersPage } from './dicerush-multipliers.js';
import { renderCrashMultipliersPage } from './crash-multipliers.js';

export interface RtpPageDeps {
  client: AdminClient;
  integratorId: string;
  gameId: string;
  roundEngineBaseUrl: string;
  /** Whether the current scope is the provider baseline (vs. a specific integrator override). */
  isProviderScope: boolean;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}

function contributorCard(gameId: string, contributors: RtpContributor[]): HTMLElement {
  const card = document.createElement('div');
  card.className = 'card';
  if (contributors.length === 0) {
    card.innerHTML =
      '<h2>Other RTP levers</h2><p class="muted">No additional payout levers affect this game’s RTP — it is fully set by the RNG weights and payout multipliers above.</p>';
    return card;
  }
  const rows = contributors
    .map(
      (c) =>
        `<tr><td><strong>${escapeHtml(c.label)}</strong></td><td class="muted">${escapeHtml(c.note)}</td>` +
        `<td style="text-align:right"><a class="ghost button" href="/games/${encodeURIComponent(gameId)}/${c.tab}">Open</a></td></tr>`,
    )
    .join('');
  card.innerHTML = `
    <h2>Other RTP levers</h2>
    <p class="muted" style="margin-top:-4px">These live on their own tabs but still move RTP. Edit them there.</p>
    <table class="data-table"><tbody>${rows}</tbody></table>`;
  return card;
}

function rtpColor(v: number | null): string {
  if (v === null) return 'var(--text-dim)';
  if (v >= 1.0) return 'var(--error)';
  if (v >= 0.97) return 'var(--warn)';
  return 'var(--success)';
}

function pct(v: number | null): string {
  return v === null ? '—' : `${(v * 100).toFixed(1)}%`;
}

/** Measured-RTP window options (hours). Backend caps at 720h (30d). Default is
 *  the widest so the headline number reflects real sample size, not 24h noise. */
const RTP_WINDOWS: ReadonlyArray<{ hours: number; label: string }> = [
  { hours: 24, label: 'last 24h' },
  { hours: 168, label: 'last 7 days' },
  { hours: 720, label: 'last 30 days' },
];
const DEFAULT_RTP_WINDOW_HOURS = 720;

/** Hero: the configured ("set") RTP up front, with measured RTP for context. */
function renderRtpHero(deps: RtpPageDeps): HTMLElement {
  const card = document.createElement('div');
  card.className = 'card rtp-hero';
  card.innerHTML = `<div class="muted">Loading RTP…</div>`;
  let windowHours = DEFAULT_RTP_WINDOW_HOURS;
  // Guard against out-of-order responses: rapidly switching the window selector
  // can let a slower earlier request resolve last and paint the wrong window.
  let reqSeq = 0;

  async function load(): Promise<void> {
    const myReq = ++reqSeq;
    try {
      // Provider "all integrators" scope routes to the baseline provider
      // integrator, which has no play of its own — a per-integrator measured RTP
      // there always reads 0. Aggregate across every integrator instead.
      const measuredReq = deps.isProviderScope
        ? deps.client.getRtpAllIntegrators(deps.gameId, windowHours)
        : deps.client.getRtp(deps.integratorId, deps.gameId, windowHours);
      const [summary, live] = await Promise.all([
        deps.client.getRtpSummary(deps.integratorId, deps.gameId),
        measuredReq.catch(() => null),
      ]);
      if (myReq !== reqSeq) return; // a newer load() superseded this one
      const set = summary.headlineRtp;
      const measured = live?.rtp ?? null;
      const breakdown = summary.perBetType
        .slice()
        .sort((a, b) => b.rtp - a.rtp)
        .map(
          (b) =>
            `<tr><td><code>${escapeHtml(b.betType)}</code></td><td style="text-align:right;color:${rtpColor(b.rtp)}">${pct(b.rtp)}</td></tr>`,
        )
        .join('');
      // For the two-tier target (greedyking), pre-empt the "why does the weights
      // sim read lower?" question right in the hero. Other games show the
      // per-bet-type breakdown instead.
      const footer =
        summary.source === 'target'
          ? `<div class="rtp-hero-info">
               <span class="rtp-hero-info-icon">i</span>
               <span>This is the <strong>total</strong> target RTP — the primary cabin draw <em>plus</em> the pool-driven special (pizza/salad) tier. The <strong>RNG weights</strong> tab tunes the <strong>primary draw only</strong>, so its dry-run reads a few points lower; the special tier makes up the rest to hit this target.</span>
             </div>`
          : breakdown
            ? `<details class="rtp-hero-breakdown"><summary class="muted">Per bet type</summary>
                 <table class="data-table" style="margin-top:8px"><thead><tr><th>Bet type</th><th style="text-align:right">Set RTP</th></tr></thead><tbody>${breakdown}</tbody></table>
               </details>`
            : '';
      card.innerHTML = `
        <div class="rtp-hero-row">
          <div class="rtp-hero-set">
            <div class="rtp-hero-eyebrow">Set RTP · ${escapeHtml(summary.headlineLabel)}</div>
            <div class="rtp-hero-value" style="color:${rtpColor(set)}">${pct(set)}</div>
            <div class="rtp-hero-note muted">${escapeHtml(summary.source === 'target' ? 'Configured target — tune the RNG weights + special tier to hit it.' : 'Ceiling across bet types, from the payout multipliers.')}</div>
          </div>
          <div class="rtp-hero-divider"></div>
          <div class="rtp-hero-measured">
            <div class="rtp-hero-eyebrow">Measured · <select class="rtp-hero-window" title="Measurement window — wider = less noise">${RTP_WINDOWS.map(
              (w) =>
                `<option value="${w.hours}"${w.hours === windowHours ? ' selected' : ''}>${w.label}</option>`,
            ).join('')}</select></div>
            <div class="rtp-hero-measured-value" style="color:${rtpColor(measured)}">${pct(measured)}</div>
            <div class="rtp-hero-note muted">${live ? `${live.rounds.toLocaleString()} rounds · ${fmtNumber(live.staked)} staked` : 'no data'}</div>
          </div>
          <button class="ghost rtp-hero-refresh" type="button" data-rtp-hero-refresh>Refresh</button>
        </div>
        ${footer}`;
      card.querySelector<HTMLSelectElement>('.rtp-hero-window')?.addEventListener('change', (e) => {
        windowHours = Number((e.target as HTMLSelectElement).value);
        void load();
      });
      card
        .querySelector<HTMLButtonElement>('[data-rtp-hero-refresh]')
        ?.addEventListener('click', () => void load());
    } catch (err) {
      if (myReq !== reqSeq) return; // superseded — don't clobber a newer render
      card.innerHTML = `<p class="error">Failed to load configured RTP: ${escapeHtml(err instanceof Error ? err.message : String(err))}</p>`;
    }
  }
  void load();
  return card;
}

/**
 * Unified "RTP & Payouts" surface. Goal-first: measured RTP + (where supported)
 * a target-RTP solver on top, then the underlying levers the solver writes to,
 * then read-only links to RTP levers on other tabs. Sections are chosen by the
 * per-game descriptor — no gameId branching here beyond picking the editor fn.
 */
export async function renderRtpPage(root: HTMLElement, deps: RtpPageDeps): Promise<void> {
  const d = descriptorFor(deps.gameId);
  root.innerHTML = `
    <h1 class="page-title">RTP &amp; Payouts — ${escapeHtml(deps.gameId)}</h1>
    <p class="page-sub">The configured RTP is shown first. Adjust the underlying levers below — sensitive changes go through two-admin approval.</p>
    <div data-rtp-root></div>
  `;
  const host = root.querySelector<HTMLElement>('[data-rtp-root]')!;

  // 1. HERO — the configured ("set") RTP, up front, always visible.
  const hero = renderRtpHero(deps);
  host.appendChild(hero);

  // 2. SECTIONS as sub-tabs — only one renders at a time, so an operator jumps
  //    between RNG weights / payout multipliers / solver / live RTP without a
  //    long vertical scroll. Panels lazy-render on first activation.
  const wDeps = {
    client: deps.client,
    integratorId: deps.integratorId,
    gameId: deps.gameId,
    roundEngineBaseUrl: deps.roundEngineBaseUrl,
  };
  const mDeps = {
    client: deps.client,
    integratorId: deps.integratorId,
    gameId: deps.gameId,
    isProviderScope: deps.isProviderScope,
  };

  interface SubTab {
    id: string;
    label: string;
    render: (panel: HTMLElement) => void | Promise<void>;
  }
  const subtabs: SubTab[] = [];

  if (d.hasSolver) {
    subtabs.push({
      id: 'solver',
      label: 'Target solver',
      render: (panel) => {
        panel.appendChild(
          renderTargetRtpPanel({
            client: deps.client,
            integratorId: deps.integratorId,
            gameId: deps.gameId,
            solverKind: d.solverKind ?? undefined,
            onApplied: async () => {
              const fresh = renderRtpHero(deps);
              hero.replaceWith(fresh);
            },
          }),
        );
      },
    });
  }
  if (d.hasWeights) {
    subtabs.push({
      id: 'weights',
      label: 'RNG weights',
      render: (panel) =>
        deps.gameId === 'sicbo'
          ? renderSicboWeightsPage(panel, wDeps)
          : deps.gameId === 'dragontiger'
            ? renderDragontigerWeightsPage(panel, wDeps)
            : renderWeightsPage(panel, wDeps),
    });
  }
  if (d.hasMultipliers) {
    subtabs.push({
      id: 'multipliers',
      // Crash has no paytable — this subtab edits its RTP constant directly.
      label: deps.gameId === 'crash' ? 'RTP setting' : 'Payout multipliers',
      render: (panel) =>
        deps.gameId === 'sicbo'
          ? renderSicboMultipliersPage(panel, mDeps)
          : deps.gameId === 'dragontiger'
            ? renderDragontigerMultipliersPage(panel, mDeps)
            : deps.gameId === 'singaporelottery'
              ? renderSingaporeLotteryMultipliersPage(panel, mDeps)
              : deps.gameId === 'baccarat'
                ? renderBaccaratMultipliersPage(panel, mDeps)
                : deps.gameId === 'shortdicerush' || deps.gameId === 'longdicerush'
                  ? renderDiceRushMultipliersPage(panel, mDeps)
                  : deps.gameId === 'crash'
                    ? renderCrashMultipliersPage(panel, mDeps)
                    : // Fallback is greedyking's food-multiplier editor. Any NEW game
                      // whose descriptor sets `hasMultipliers` needs its own branch
                      // here, or it silently renders carrots/steak rows against its
                      // own `multipliers` surface — which is exactly how baccarat
                      // (and later crash) shipped with a greedyking paytable editor.
                      renderMultipliersPage(panel, mDeps),
    });
  }
  subtabs.push({
    id: 'monitor',
    label: 'Live RTP',
    render: (panel) => {
      panel.appendChild(
        renderLiveRtpCard({
          client: deps.client,
          integratorId: deps.integratorId,
          gameId: deps.gameId,
        }),
      );
    },
  });
  if (d.contributors.length > 0) {
    subtabs.push({
      id: 'related',
      label: 'Related levers',
      render: (panel) => {
        panel.appendChild(contributorCard(deps.gameId, d.contributors));
      },
    });
  }

  const nav = document.createElement('div');
  nav.className = 'rtp-subtabs';
  nav.setAttribute('role', 'tablist');
  const panelsWrap = document.createElement('div');
  host.appendChild(nav);
  host.appendChild(panelsWrap);

  const panels = new Map<string, HTMLElement>();
  const loaded = new Set<string>();
  for (const t of subtabs) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'rtp-subtab';
    btn.dataset.sub = t.id;
    btn.textContent = t.label;
    btn.addEventListener('click', () => void activate(t.id));
    nav.appendChild(btn);
    const panel = document.createElement('div');
    panel.className = 'rtp-panel hidden';
    panel.dataset.subpanel = t.id;
    panels.set(t.id, panel);
    panelsWrap.appendChild(panel);
  }

  async function activate(id: string): Promise<void> {
    nav
      .querySelectorAll<HTMLButtonElement>('.rtp-subtab')
      .forEach((b) => b.classList.toggle('active', b.dataset.sub === id));
    panels.forEach((p, k) => p.classList.toggle('hidden', k !== id));
    if (!loaded.has(id)) {
      loaded.add(id);
      const t = subtabs.find((s) => s.id === id);
      const panel = panels.get(id);
      if (t && panel) {
        panel.innerHTML = '<p class="muted">Loading…</p>';
        panel.innerHTML = '';
        await t.render(panel);
      }
    }
  }

  if (subtabs[0]) await activate(subtabs[0].id);
}
